import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { CreateDocumentaryRequest, Category, DocumentaryGenre, MediaLink, Language } from '@/types/model';
import { useTranslation } from '@/hooks';
import { useCategoriesForDropdown } from '@/hooks';
import { Translation } from '@/locales';
import Input from '@/components/ui/Input';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import MultiSelect from '@/components/ui/MultiSelect';
import LinkManager from '@/components/ui/LinkManager';
import MediaAssetsSection from '@/components/ui/MediaAssetsSection';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import { classificationOptions, documentaryGenres, qualityTypes } from '@/constants';
import ImageNotAvailable from '@/assets/image-not-available.png';
import { isDigitalOceanSpacesUrl } from '@/utils/validation.ts';
import { MediaSource } from '@/types/media';

const createDocumentarySchema = (t: Translation) => z.object({
  title: z
    .string()
    .min(3, t.validation.minLength(3))
    .max(200, t.validation.maxLength(200)),
  year: z
    .number({
      message: 'Año es requerido'
    })
    .min(1900, 'Año debe ser al menos 1900')
    .max(2100, 'Año no puede exceder 2100'),
  releaseDate: z.string().optional(),
  durationMinutes: z
    .number({
      message: 'Duración es requerida'
    })
    .min(1, 'Duración debe ser al menos 1 minuto')
    .max(999, 'Duración no puede exceder 999 minutos'),
  spanishSynopsis: z
    .string()
    .max(10000, 'Sinopsis en español no puede exceder 10000 caracteres')
    .optional(),
  englishSynopsis: z
    .string()
    .max(10000, 'Sinopsis en inglés no puede exceder 10000 caracteres')
    .optional(),
  classification: z.enum(['G', 'PG', 'PG_13', 'R', 'NC_17', 'NR']),
  genres: z.array(z.string()).optional(),
  categoryIds: z.array(z.string()).optional(),
  coverImageUrl: z
    .url('URL de imagen de portada inválida')
    .or(z.literal('')),
  backgroundImageUrl: z
    .url('URL de imagen de fondo inválida')
    .or(z.literal('')),
  trailerUrl: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine((value) => !value || isDigitalOceanSpacesUrl(value), {
      message: 'Por favor suba el trailer a Digital Ocean',
    }),
  reference: z
    .string()
    .max(100, 'Referencia no puede exceder 100 caracteres')
    .optional(),
  active: z.boolean(),
  monoAudio: z.boolean(),
  englishOnly: z.boolean(),
  kidsContent: z.boolean(),
  links: z.array(
    z.object({
      id: z.string().optional(),
      quality: z.enum(qualityTypes),
      url: z.url(t.validation.urlFormat),
      audioLanguages: z.array(z.string()).optional(),
      subtitleLanguages: z.array(z.string()).optional(),
      active: z.boolean(),
    }), t.links.required
  ).min(1, t.links.required)
});

type DocumentaryFormData = z.infer<ReturnType<typeof createDocumentarySchema>>;

interface DocumentaryFormProps {
  onSuccess?: (data: CreateDocumentaryRequest) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateDocumentaryRequest>;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const DocumentaryForm = ({
  onSuccess,
  onCancel,
  initialData,
  isEdit = false,
  isSubmitting = false
}: DocumentaryFormProps) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialData?.categoryIds || []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<DocumentaryFormData>({
    resolver: zodResolver(createDocumentarySchema(t)),
    defaultValues: {
      title: initialData?.title || '',
      year: initialData?.year || new Date().getFullYear(),
      releaseDate: initialData?.releaseDate || '',
      durationMinutes: initialData?.durationMinutes || 0,
      spanishSynopsis: initialData?.spanishSynopsis || '',
      englishSynopsis: initialData?.englishSynopsis || '',
      classification: initialData?.classification || 'G',
      genres: initialData?.genres || [],
      categoryIds: initialData?.categoryIds || [],
      coverImageUrl: initialData?.coverImageUrl || '',
      backgroundImageUrl: initialData?.backgroundImageUrl || '',
      trailerUrl: initialData?.trailerUrl || '',
      reference: initialData?.reference || '',
      active: initialData?.active ?? true,
      monoAudio: initialData?.monoAudio ?? false,
      englishOnly: initialData?.englishOnly ?? false,
      kidsContent: initialData?.kidsContent ?? false,
      links: initialData?.links || []
    }
  });

  const genreOptions = documentaryGenres.map((genre) => ({
    value: genre.toString(),
    label: t.documentaries.genres[genre as keyof typeof t.documentaries.genres] || genre.toString()
  }));

  const useCategoriesForDocumentaryDropdown = () =>
    useCategoriesForDropdown('DOCUMENTARY');
  const { data: categoriesData } = useCategoriesForDocumentaryDropdown();

  const mapCategoriesToOptions = (data: unknown[]) =>
    (data as Category[]).map(cat => ({ value: cat.id, label: cat.description }));

  const onSubmit = async (data: DocumentaryFormData) => {
    setSubmitError(null);

    try {
      const formattedData: CreateDocumentaryRequest = {
        ...data,
        releaseDate: data.releaseDate ? new Date(data.releaseDate).toISOString().split('T')[0] : undefined,
        genres: data.genres as DocumentaryGenre[] | undefined,
        links: data.links?.map(link => ({
          ...link,
          id: link.id || crypto.randomUUID(),
          audioLanguages: link.audioLanguages as Language[],
          subtitleLanguages: link.subtitleLanguages as Language[],
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString()
        }))
      };
      await onSuccess?.(formattedData);
      if (!isEdit) {
        reset();
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const watchedValues = watch(['active', 'monoAudio', 'englishOnly', 'kidsContent', 'coverImageUrl', 'backgroundImageUrl', 'trailerUrl', 'genres', 'links']);
  const [active, monoAudio, englishOnly, kidsContent, coverImageUrl, backgroundImageUrl, trailerUrl, selectedGenres, watchedLinks] = watchedValues;


  const handleBackgroundImageChange = (url: string) => {
    setValue('backgroundImageUrl', url);
  };

  const handleTrailerUrlChange = (url: string) => {
    setValue('trailerUrl', url);
  };


  const handleAddLink = async (link: Omit<MediaLink, 'id' | 'createdDate' | 'updatedDate'>) => {
    try {
      const newLink = {
        ...link,
        id: crypto.randomUUID(),
        audioLanguages: link.audioLanguages?.map(a => a.toUpperCase()) || [],
        subtitleLanguages: link.subtitleLanguages?.map(s => s.toUpperCase()) || [],
      };
      setValue('links', [...(watchedLinks || []), newLink]);
    } catch (error) {
      console.error('Error adding link:', error);
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    try {
      setValue('links', (watchedLinks || []).filter(link => link.id !== linkId));
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  const handleUpdateLink = async (linkId: string, updatedLink: Omit<MediaLink, 'id' | 'createdDate' | 'updatedDate'>) => {
    try {
      const updatedLinks = (watchedLinks || []).map(link =>
        link.id === linkId
          ? {
              ...link,
              ...updatedLink,
              audioLanguages: updatedLink.audioLanguages?.map(a => a.toUpperCase()) || [],
              subtitleLanguages: updatedLink.subtitleLanguages?.map(s => s.toUpperCase()) || [],
            }
          : link
      );
      setValue('links', updatedLinks);
    } catch (error) {
      console.error('Error updating link:', error);
    }
  };

  const handleToggleLink = async (linkId: string, active: boolean) => {
    try {
      const updatedLinks = (watchedLinks || []).map(link =>
        link.id === linkId ? { ...link, active } : link
      );
      setValue('links', updatedLinks);
    } catch (error) {
      console.error('Error toggling link:', error);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {isEdit ? 'Editar Documental' : 'Crear Documental'}
        </h2>
        <p className="text-gray-600 mt-1">
          {isEdit ? 'Actualiza la información del documental' : 'Completa la información del nuevo documental'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Top Section with Cover Image */}
        <div className="flex gap-6">
          {/* Left Side - Form Fields */}
          <div className="flex-1 space-y-4">
            <Input
              label="Título"
              placeholder="Ingresa el título del documental"
              error={errors.title?.message}
              {...register('title')}
            />

            <Input
              label="Año"
              type="number"
              min="1900"
              max="2100"
              placeholder="2023"
              error={errors.year?.message}
              {...register('year', { valueAsNumber: true })}
            />

            <ImageUrlUploadField
              label="Imagen Cover"
              placeholder="https://example.com/cover.jpg"
              value={coverImageUrl || ''}
              onChange={(url) => setValue('coverImageUrl', url, { shouldValidate: true, shouldDirty: true })}
              error={errors.coverImageUrl?.message}
              disabled={isSubmitting}
              source={MediaSource.DOCUMENTARY_POSTER}
              showPreview={false}
            />
          </div>

          {/* Right Side - Cover Image Preview */}
          <div className="flex-shrink-0">
            {coverImageUrl ? (
              <img
                src={coverImageUrl}
                alt="Vista previa de la portada"
                className="h-58 w-40 object-cover rounded-lg shadow-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = ImageNotAvailable;
                  target.style.display = 'block';
                }}
                onLoad={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'block';
                }}
              />
            ) : (
              <div
                className="h-58 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <div className="text-xs">Vista previa</div>
                  <div className="text-xs">de portada</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Fecha Lanzamiento"
            type="date"
            error={errors.releaseDate?.message}
            {...register('releaseDate')}
          />

          <Input
            label="Duración (Min)"
            type="number"
            min="1"
            max="999"
            placeholder="90"
            error={errors.durationMinutes?.message}
            {...register('durationMinutes', { valueAsNumber: true })}
          />

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Sinopsis-Español</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              rows={4}
              placeholder="Describe el documental en español"
              {...register('spanishSynopsis')}
            />
            {errors.spanishSynopsis && (
              <p className="mt-1 text-sm text-red-600">{errors.spanishSynopsis.message}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Sinopsis-Inglés</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              rows={4}
              placeholder="Describe el documental en inglés"
              {...register('englishSynopsis')}
            />
            {errors.englishSynopsis && (
              <p className="mt-1 text-sm text-red-600">{errors.englishSynopsis.message}</p>
            )}
          </div>

          <Select
            label="Clasificación"
            options={classificationOptions}
            error={errors.classification?.message}
            {...register('classification')}
          />

          <MultiSelect
            label="Género"
            placeholder="Selecciona géneros..."
            options={genreOptions}
            value={selectedGenres || []}
            onChange={(genres) => setValue('genres', genres)}
            error={errors.genres?.message}
          />

          <MultiSelect
            label="Categorías"
            placeholder={t.tvChannels.placeholders.categories}
            options={mapCategoriesToOptions(categoriesData || [])}
            value={selectedCategories}
            onChange={(newCategories) => {
              setSelectedCategories(newCategories);
              setValue('categoryIds', newCategories);
            }}
            error={errors.categoryIds?.message}
          />


          <div className="md:col-span-2">
          </div>

          {/* Media Assets Section */}
          <MediaAssetsSection
            backgroundImageUrl={backgroundImageUrl || ''}
            trailerUrl={trailerUrl || ''}
            onBackgroundImageChange={handleBackgroundImageChange}
            onTrailerUrlChange={handleTrailerUrlChange}
            backgroundImageSource={MediaSource.DOCUMENTARY_BACKGROUND}
            backgroundImageError={errors.backgroundImageUrl?.message}
            trailerUrlError={errors.trailerUrl?.message}
          />

          <Input
            label="Referencia"
            placeholder="Referencia opcional"
            error={errors.reference?.message}
            {...register('reference')}
          />

          <div className="md:col-span-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estado:</label>
                <Toggle
                  checked={active}
                  onChange={(checked) => setValue('active', checked)}
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mono audio:</label>
                <Toggle
                  checked={monoAudio}
                  onChange={(checked) => setValue('monoAudio', checked)}
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Solo Inglés:</label>
                <Toggle
                  checked={englishOnly}
                  onChange={(checked) => setValue('englishOnly', checked)}
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Para niño:</label>
                <Toggle
                  checked={kidsContent}
                  onChange={(checked) => setValue('kidsContent', checked)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>

        <LinkManager
          links={watchedLinks || []}
          onAddLink={handleAddLink}
          onUpdateLink={handleUpdateLink}
          onDeleteLink={handleDeleteLink}
          onToggleLink={handleToggleLink}
          error={errors.links?.message}
        />

        {submitError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t.buttons.cancel}
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Creando...'
              : isEdit
                ? 'Actualizar Documental'
                : 'Crear Documental'
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DocumentaryForm;
